import React, { useState, useEffect } from 'react';
import { apiPost, apiGet } from '../utils/api';
import { Calculator, Lock, Hash, FileText, Copy, RefreshCw, Check, Shield, Link, Type, Network, Percent, Timer, Phone, Thermometer, CreditCard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import MacLookup from './MacLookup';
import MacGenerator from './MacGenerator';
import IPv6ULAGenerator from './IPv6ULAGenerator';
import MathEvaluator from './MathEvaluator';
import EtaCalculator from './EtaCalculator';
import PercentageCalculator from './PercentageCalculator';
import Chronometer from './Chronometer';
import PhoneValidator from './utils/PhoneValidator';
import IbanValidator from './utils/IbanValidator';

const Utils = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [calculatorDisplay, setCalculatorDisplay] = useState('0');
  const [calculatorHistory, setCalculatorHistory] = useState('');
  const [passwordLength, setPasswordLength] = useState(12);
  const [passwordOptions, setPasswordOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [hashInput, setHashInput] = useState('');
  const [hashResult, setHashResult] = useState('');
  const [base64Input, setBase64Input] = useState('');
  const [base64Result, setBase64Result] = useState('');
  const [base64Mode, setBase64Mode] = useState('encode');
  const [uuid, setUuid] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [urlResult, setUrlResult] = useState('');
  const [urlMode, setUrlMode] = useState('encode');
  const [loremCount, setLoremCount] = useState(3);
  const [loremText, setLoremText] = useState('');

  // Definição das abas e rotas
  const tabs = [
    { id: 'calculator', name: 'Calculadora', icon: Calculator, path: '/utils/calculator', description: 'Calculadora completa', status: 'active' },
    { id: 'password-generator', name: 'Gerador de Senha', icon: Lock, path: '/utils/password-generator', description: 'Senhas seguras', status: 'active' },
    { id: 'hash', name: 'Hash', icon: Hash, path: '/utils/hash', description: 'MD5, SHA1, SHA256', status: 'active' },
    { id: 'base64', name: 'Base64', icon: FileText, path: '/utils/base64', description: 'Codificar/Decodificar', status: 'active' },
    { id: 'uuid', name: 'Gerador de UUID', icon: Hash, path: '/utils/uuid', description: 'Gerador de UUID', status: 'active' },
    { id: 'url-encoder', name: 'URL Encoder', icon: Link, path: '/utils/url-encoder', description: 'Codificar URLs', status: 'active' },
    { id: 'lorem-ipsum', name: 'Lorem Ipsum', icon: Type, path: '/utils/lorem-ipsum', description: 'Gerador de texto', status: 'active' },
    { id: 'mac-lookup', name: 'Consulta de MAC Address', icon: Network, path: '/utils/mac-lookup', description: 'Descubra o fabricante pelo endereço MAC', status: 'active' },
    { id: 'mac-generator', name: 'Gerador de MAC Address', icon: Network, path: '/utils/mac-generator', description: 'Gere endereços MAC aleatórios', status: 'active' },
    { id: 'ipv6-ula-generator', name: 'Gerador de IPv6 ULA', icon: Network, path: '/utils/ipv6-ula-generator', description: 'Gere endereços IPv6 ULA aleatórios', status: 'active' },
    { id: 'math-evaluator', name: 'Calculadora Avançada', icon: Calculator, path: '/utils/math-evaluator', description: 'Avalie expressões matemáticas', status: 'active' },
    { id: 'eta-calculator', name: 'Calculadora de ETA', icon: RefreshCw, path: '/utils/eta-calculator', description: 'Calcule tempo estimado de chegada', status: 'active' },
    { id: 'percentage-calculator', name: 'Calculadora de Porcentagem', icon: Percent, path: '/utils/percentage-calculator', description: 'Calcule porcentagens facilmente', status: 'active' },
    { id: 'chronometer', name: 'Cronômetro', icon: Timer, path: '/utils/chronometer', description: 'Cronômetro simples', status: 'active' },
    { id: 'phone-validator', name: 'Validador de Telefone', icon: Phone, path: '/utils/phone-validator', description: 'Validação de números', status: 'active' },
    { id: 'iban-validator', name: 'Validador de IBAN', icon: CreditCard, path: '/utils/iban-validator', description: 'Validação de IBAN', status: 'active' },
    { id: 'user-agent-finder', name: 'User Agent Finder', icon: Hash, path: '/utils/user-agent-finder', description: 'Descubra o seu agente de usuário', status: 'active' },
    { id: 'my-ip', name: 'Qual é Meu IP?', icon: Hash, path: '/utils/my-ip', description: 'Descubra o seu endereço IP público', status: 'active' },
    { id: 'ping', name: 'Ping', icon: Hash, path: '/utils/ping', description: 'Medir o ping para qualquer endereço', status: 'active' },
    { id: 'unshorten-url', name: 'Achar URL Encurtada', icon: Hash, path: '/utils/unshorten-url', description: 'Descubra a URL original de links encurtados', status: 'active' },
  ];

  // Detectar aba ativa pela URL
  const getActiveTab = () => {
    const match = tabs.find(tab => location.pathname.startsWith(tab.path));
    return match ? match.id : 'calculator';
  };
  const activeTab = getActiveTab();

  // Redirecionar /utils para /utils/calculator
  useEffect(() => {
    if (location.pathname === '/utils' || location.pathname === '/utils/') {
      navigate('/utils/calculator', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Calculadora
  const handleCalculatorClick = (value) => {
    if (value === 'C') {
      setCalculatorDisplay('0');
      setCalculatorHistory('');
    } else if (value === '=') {
      try {
        const result = eval(calculatorHistory + calculatorDisplay);
        setCalculatorDisplay(result.toString());
        setCalculatorHistory('');
      } catch (error) {
        setCalculatorDisplay('Error');
      }
    } else if (['+', '-', '*', '/'].includes(value)) {
      setCalculatorHistory(calculatorDisplay + value);
      setCalculatorDisplay('0');
    } else {
      if (calculatorDisplay === '0') {
        setCalculatorDisplay(value);
      } else {
        setCalculatorDisplay(calculatorDisplay + value);
      }
    }
  };

  // Gerador de Senha
  const generatePassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    let chars = '';
    if (passwordOptions.uppercase) chars += uppercase;
    if (passwordOptions.lowercase) chars += lowercase;
    if (passwordOptions.numbers) chars += numbers;
    if (passwordOptions.symbols) chars += symbols;

    if (chars === '') {
      toast.error('Selecione pelo menos uma opção');
      return;
    }

    let password = '';
    for (let i = 0; i < passwordLength; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    setGeneratedPassword(password);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  // Hash
  const generateHash = async () => {
    if (!hashInput.trim()) {
      toast.error('Digite um texto para gerar o hash');
      return;
    }

    try {
      const data = await apiPost('/api/utils/hash', { text: hashInput });
      setHashResult(data.hash);
    } catch (error) {
      toast.error('Erro ao gerar hash');
    }
  };

  // Base64
  const handleBase64Conversion = async () => {
    if (!base64Input.trim()) {
      toast.error('Digite um texto para converter');
      return;
    }

    try {
      const data = await apiPost('/api/utils/base64', { text: base64Input, mode: base64Mode });
      setBase64Result(data.result);
    } catch (error) {
      toast.error('Erro na conversão Base64');
    }
  };

  const generateUuid = () => {
    const newUuid = uuidv4();
    setUuid(newUuid);
  };

  const handleUrlConversion = () => {
    if (!urlInput.trim()) {
      toast.error('Digite um texto para converter');
      return;
    }
    if (urlMode === 'encode') {
      setUrlResult(encodeURIComponent(urlInput));
    } else {
      setUrlResult(decodeURIComponent(urlInput));
    }
  };

  const generateLorem = () => {
    const paragraph = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed euismod, nunc ut laoreet gravida, urna sem cursus massa, a hendrerit purus enim nec massa.';
    setLoremText(Array.from({ length: loremCount }, () => paragraph).join('\n\n'));
  };

  const Placeholder = ({ title }) => (
    <div className="bg-white rounded-lg shadow-lg p-6 text-center text-gray-500 text-xl">
      {title} - Em breve
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas Utilitárias
        </h1>
        <p className="text-gray-600">
          Calculadora, gerador de senhas, hash e conversor Base64
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="calculator" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-xs mx-auto">
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <div className="text-right text-sm text-gray-500 mb-1">
                  {calculatorHistory}
                </div>
                <div className="text-right text-2xl font-mono text-gray-900">
                  {calculatorDisplay}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {['C', '±', '%', '÷', '7', '8', '9', '×', '4', '5', '6', '-', '1', '2', '3', '+', '0', '.', '='].map((btn) => (
                  <button
                    key={btn}
                    onClick={() => handleCalculatorClick(btn === '÷' ? '/' : btn === '×' ? '*' : btn)}
                    className={`p-4 rounded-lg font-medium transition-all duration-200 ${
                      btn === '=' || btn === '÷' || btn === '×' || btn === '-' || btn === '+'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : btn === 'C'
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    }`}
                  >
                    {btn}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )} />
        <Route path="password-generator" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-md mx-auto space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comprimento da Senha
                </label>
                <input
                  type="range"
                  min="4"
                  max="50"
                  value={passwordLength}
                  onChange={(e) => setPasswordLength(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="text-center text-sm text-gray-600 mt-1">
                  {passwordLength} caracteres
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Opções
                </label>
                {Object.entries(passwordOptions).map(([key, value]) => (
                  <label key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setPasswordOptions({ ...passwordOptions, [key]: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">
                      {key === 'uppercase' ? 'Maiúsculas' : key === 'lowercase' ? 'Minúsculas' : key === 'numbers' ? 'Números' : 'Símbolos'}
                    </span>
                  </label>
                ))}
              </div>

              <button
                onClick={generatePassword}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <RefreshCw size={16} />
                <span>Gerar Senha</span>
              </button>

              {generatedPassword && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono text-gray-900 break-all">
                      {generatedPassword}
                    </code>
                    <button
                      onClick={() => copyToClipboard(generatedPassword)}
                      className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )} />
        <Route path="uuid" element={(
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
            <div className="flex flex-col items-center space-y-4">
              <button
                onClick={generateUuid}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <RefreshCw size={16} />
                <span>Gerar UUID</span>
              </button>
              {uuid && (
                <div className="bg-gray-50 p-4 rounded-lg w-full flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-900 break-all">{uuid}</code>
                  <button onClick={() => copyToClipboard(uuid)} className="ml-2 p-1 text-gray-500 hover:text-gray-700"><Copy size={16} /></button>
                </div>
              )}
            </div>
          </div>
        )} />
        <Route path="hash" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texto para Hash
                </label>
                <textarea
                  value={hashInput}
                  onChange={(e) => setHashInput(e.target.value)}
                  placeholder="Digite o texto que deseja converter em hash..."
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={generateHash}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Gerar Hash
              </button>

              {hashResult && (
                <div className="space-y-4">
                  {Object.entries(hashResult).map(([algorithm, hash]) => (
                    <div key={algorithm} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 capitalize">
                          {algorithm}
                        </span>
                        <button
                          onClick={() => copyToClipboard(hash)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                      <code className="text-xs font-mono text-gray-900 break-all">
                        {hash}
                      </code>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )} />
        <Route path="base64" element={(
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="flex space-x-2">
                <button
                  onClick={() => setBase64Mode('encode')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    base64Mode === 'encode'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  Codificar
                </button>
                <button
                  onClick={() => setBase64Mode('decode')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    base64Mode === 'decode'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  Decodificar
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {base64Mode === 'encode' ? 'Texto para Codificar' : 'Base64 para Decodificar'}
                </label>
                <textarea
                  value={base64Input}
                  onChange={(e) => setBase64Input(e.target.value)}
                  placeholder={base64Mode === 'encode' ? 'Digite o texto que deseja codificar...' : 'Digite o Base64 que deseja decodificar...'}
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={handleBase64Conversion}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                {base64Mode === 'encode' ? 'Codificar' : 'Decodificar'}
              </button>

              {base64Result && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Resultado
                    </span>
                    <button
                      onClick={() => copyToClipboard(base64Result)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                  <code className="text-sm font-mono text-gray-900 break-all">
                    {base64Result}
                  </code>
                </div>
              )}
            </div>
          </div>
        )} />
        <Route path="url-encoder" element={(
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
            <div className="flex space-x-2 mb-4">
              <button onClick={() => setUrlMode('encode')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${urlMode === 'encode' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>Codificar</button>
              <button onClick={() => setUrlMode('decode')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${urlMode === 'decode' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>Decodificar</button>
            </div>
            <textarea value={urlInput} onChange={e => setUrlInput(e.target.value)} placeholder={urlMode === 'encode' ? 'Digite o texto para codificar...' : 'Digite a URL codificada para decodificar...'} className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            <button onClick={handleUrlConversion} className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200">{urlMode === 'encode' ? 'Codificar' : 'Decodificar'}</button>
            {urlResult && (
              <div className="bg-gray-50 p-4 rounded-lg mt-4 flex items-center justify-between">
                <code className="text-sm font-mono text-gray-900 break-all">{urlResult}</code>
                <button onClick={() => copyToClipboard(urlResult)} className="ml-2 p-1 text-gray-500 hover:text-gray-700"><Copy size={16} /></button>
              </div>
            )}
          </div>
        )} />
        <Route path="lorem-ipsum" element={(
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Quantidade de parágrafos</label>
              <input type="number" min={1} max={10} value={loremCount} onChange={e => setLoremCount(Number(e.target.value))} className="w-24 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <button onClick={generateLorem} className="ml-4 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200">Gerar</button>
            </div>
            {loremText && (
              <div className="space-y-4">
                {loremText.split('\n\n').map((p, i) => <p key={i} className="text-gray-800">{p}</p>)}
              </div>
            )}
          </div>
        )} />
        <Route path="mac-lookup" element={<MacLookup />} />
        <Route path="mac-generator" element={<MacGenerator />} />
        <Route path="ipv6-ula-generator" element={<IPv6ULAGenerator />} />
        <Route path="math-evaluator" element={<MathEvaluator />} />
        <Route path="eta-calculator" element={<EtaCalculator />} />
        <Route path="percentage-calculator" element={<PercentageCalculator />} />
        <Route path="chronometer" element={<Chronometer />} />
        <Route path="phone-validator" element={<PhoneValidator />} />
        <Route path="iban-validator" element={<IbanValidator />} />
        <Route path="user-agent-finder" element={<UserAgentFinder />} />
        <Route path="my-ip" element={<MyIP />} />
        <Route path="ping" element={<PingTool />} />
        <Route path="unshorten-url" element={<UnshortenUrl />} />
      </Routes>
    </div>
  );
};

const UserAgentFinder = () => {
  const [userAgent, setUserAgent] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchUserAgent = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/utils/user-agent-finder');
      setUserAgent(data.userAgent);
    } catch (e) {
      setUserAgent('Erro ao obter user agent');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAgent();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-gray-900">User Agent Finder</h2>
      <p className="mb-4 text-gray-600">Descubra o seu agente de usuário (user agent) do navegador.</p>
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm text-gray-800 break-all">
        {loading ? 'Carregando...' : userAgent}
      </div>
    </div>
  );
};

const MyIP = () => {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchIp = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/utils/my-ip');
      setIp(data.ip);
    } catch (e) {
      setIp('Erro ao obter IP');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchIp();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-gray-900">Qual é Meu IP?</h2>
      <p className="mb-4 text-gray-600">Descubra o seu endereço IP público.</p>
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-lg text-gray-800 break-all">
        {loading ? 'Carregando...' : ip}
      </div>
    </div>
  );
};

const PingTool = () => {
  const [address, setAddress] = useState('google.com');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePing = async () => {
    if (!address) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await apiPost('/api/utils/ping', { address });
      setResult(data);
    } catch (e) {
      setResult({ error: 'Erro ao executar ping' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-gray-900">Ping</h2>
      <p className="mb-4 text-gray-600">Meça o ping para qualquer endereço (host ou IP).</p>
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={address}
          onChange={e => setAddress(e.target.value)}
          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Ex: google.com"
        />
        <button
          onClick={handlePing}
          disabled={loading || !address}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          {loading ? 'Testando...' : 'Ping'}
        </button>
      </div>
      {result && (
        <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm text-gray-800 break-all">
          {result.error ? (
            <span className="text-red-600">{result.error}</span>
          ) : (
            <>
              <div><b>Host:</b> {result.host}</div>
              <div><b>IP:</b> {result.ip}</div>
              <div><b>Online:</b> {result.alive ? 'Yes' : 'No'}</div>
              <div><b>Time:</b> {result.time} ms</div>
              <div><b>Min:</b> {result.min} ms</div>
              <div><b>Max:</b> {result.max} ms</div>
              <div><b>Avg:</b> {result.avg} ms</div>
              <div><b>Packet Loss:</b> {result.packetLoss}%</div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const UnshortenUrl = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleUnshorten = async () => {
    if (!input) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await apiPost('/api/utils/unshorten-url', { url: input });
      setResult(data);
    } catch (e) {
      setResult({ error: 'Erro ao buscar URL original' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-gray-900">Achar URL Encurtada</h2>
      <p className="mb-4 text-gray-600">Insira um link encurtado (bit.ly, tinyurl, etc) e descubra a URL original.</p>
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Cole aqui a URL encurtada..."
        />
        <button
          onClick={handleUnshorten}
          disabled={loading || !input}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          {loading ? 'Buscando...' : 'Achar' }
        </button>
      </div>
      {result && (
        <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm text-gray-800 break-all">
          {result.error ? (
            <span className="text-red-600">{result.error}</span>
          ) : (
            <>
              <div><b>Original:</b> {result.original}</div>
              <div><b>URL Final:</b> <a href={result.unshortened} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{result.unshortened}</a></div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'calculator', name: 'Calculadora', path: '/utils/calculator' },
    { id: 'password-generator', name: 'Gerador de Senha', path: '/utils/password-generator' },
    { id: 'hash', name: 'Hash', path: '/utils/hash' },
    { id: 'base64', name: 'Base64', path: '/utils/base64' },
    { id: 'uuid', name: 'Gerador de UUID', path: '/utils/uuid' },
    { id: 'url-encoder', name: 'URL Encoder', path: '/utils/url-encoder' },
    { id: 'lorem-ipsum', name: 'Lorem Ipsum', path: '/utils/lorem-ipsum' },
    { id: 'mac-lookup', name: 'Consulta de MAC Address', path: '/utils/mac-lookup' },
    { id: 'mac-generator', name: 'Gerador de MAC Address', path: '/utils/mac-generator' },
    { id: 'ipv6-ula-generator', name: 'Gerador de IPv6 ULA', path: '/utils/ipv6-ula-generator' },
    { id: 'math-evaluator', name: 'Calculadora Avançada', path: '/utils/math-evaluator' },
    { id: 'eta-calculator', name: 'Calculadora de ETA', path: '/utils/eta-calculator' },
    { id: 'percentage-calculator', name: 'Calculadora de Porcentagem', path: '/utils/percentage-calculator' },
    { id: 'chronometer', name: 'Cronômetro', path: '/utils/chronometer' },
    { id: 'phone-validator', name: 'Validador de Telefone', path: '/utils/phone-validator' },
    { id: 'iban-validator', name: 'Validador de IBAN', path: '/utils/iban-validator' },
    { id: 'user-agent-finder', name: 'User Agent Finder', path: '/utils/user-agent-finder' },
    { id: 'my-ip', name: 'Qual é Meu IP?', path: '/utils/my-ip' },
    { id: 'ping', name: 'Ping', path: '/utils/ping' },
    { id: 'unshorten-url', name: 'Achar URL Encurtada', path: '/utils/unshorten-url' },
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Utilidades', tool: match ? match.name : null };
}

export default Utils; 